import {
  IconDeviceFloppy,
  IconEdit,
  IconPlus,
  IconSearch,
  IconX,
} from '@tabler/icons-react'
import { useCallback, useEffect, useMemo, useState } from 'react'

import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Separator } from '@/components/ui/separator'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { cn } from '@/utils'
import type {
  EditableTableColumn,
  EditableTableProps,
  RowStatus,
} from './tableTypes'
import { Pagination } from './tableUtils'

export function EditableTable<T extends { id: string }>({
  title,
  description,
  formMethods,
  formFieldName,
  columns,
  filters = [],
  summaryItems = [],
  itemsPerPage = 6,
  tableHeight = 600,
  onSave,
  onSubmit: onSubmitProp,
  onCancel,
  onEdit,
  onAddRow,
  onCloneRow,
  onDeleteRow,
  isEditMode,
  editedRows,
  newRows,
  deletedRows,
  setEditedRows: _setEditedRows,
  setNewRows,
  setDeletedRows,
  showStatusIndicator = true,
  statusIndicatorWidth: _statusIndicatorWidth = '10px',
  leftStickyColumns: _leftStickyColumns = 0,
  emptyMessage = 'No data available',
  addButtonLabel = 'Add Row',
  showDetailedErrors = false,
  getRowName,
  renderActions,
}: EditableTableProps<T>) {
  // Pagination
  const [currentPage, setCurrentPage] = useState(1)

  const {
    register,
    watch,
    setValue,
    trigger,
    handleSubmit,
    formState: { errors },
  } = formMethods

  // Watch for changes
  const watchedData = watch()

  // Helper to get data array from form
  const getDataArray = (formData: any): T[] => {
    if (formFieldName) {
      return formData[formFieldName] || []
    }
    return Array.isArray(formData) ? formData : []
  }

  // Get current data array from form
  const currentData = useMemo(() => {
    return getDataArray(watchedData)
  }, [watchedData, formFieldName])

  // Filter data
  const filteredData = useMemo(() => {
    return currentData.filter((row: T) => {
      // Hide deleted rows in read mode
      if (!isEditMode && deletedRows.has(row.id)) return false

      // Apply filters
      return filters.every((filter) => {
        if (filter.type === 'search' && filter.value) {
          const searchLower = filter.value.toLowerCase()
          return Object.values(row as any).some((val) =>
            String(val).toLowerCase().includes(searchLower)
          )
        }
        if (filter.type === 'select' && filter.value !== 'all') {
          // Use custom filter function if provided
          if (filter.customFilter) {
            return filter.customFilter(row, filter.value)
          }

          // Otherwise use accessorKey
          const filterKey = filter.accessorKey
          if (filterKey) {
            return (row as any)[filterKey] === filter.value
          }
        }
        return true
      })
    })
  }, [currentData, filters, deletedRows, isEditMode])

  // Pagination
  const { paginatedData, totalPages } = useMemo(() => {
    const total = Math.ceil(filteredData.length / itemsPerPage)
    const startIndex = (currentPage - 1) * itemsPerPage
    const endIndex = startIndex + itemsPerPage
    const paginated = filteredData.slice(startIndex, endIndex)

    return { paginatedData: paginated, totalPages: total || 1 }
  }, [filteredData, currentPage, itemsPerPage])

  const handlePageChange = (page: number) => {
    setCurrentPage(page)
  }

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1)
  }, [filters])

  // Validation errors
  const validationErrorCount = useMemo(() => {
    if (
      !errors ||
      !errors[formFieldName] ||
      !Array.isArray(errors[formFieldName])
    ) {
      return 0
    }
    return errors[formFieldName].filter((error: any) => error).length
  }, [errors, formFieldName])

  const hasValidationErrors = validationErrorCount > 0

  // Detailed validation errors (for showDetailedErrors prop)
  const validationErrorDetails = useMemo(() => {
    if (!errors || !errors[formFieldName]) return []

    const errorList: Array<{
      rowIndex: number
      fieldName: string
      message: string
      rowName: string
    }> = []

    Object.entries(errors[formFieldName]).forEach(([index, rowErrors]) => {
      if (rowErrors && typeof rowErrors === 'object') {
        const row = currentData[parseInt(index)]
        const rowName = getRowName
          ? getRowName(row, parseInt(index))
          : `Row ${parseInt(index) + 1}`

        Object.entries(rowErrors).forEach(([field, error]) => {
          if (error && typeof error === 'object' && 'message' in error) {
            errorList.push({
              rowIndex: parseInt(index),
              fieldName: field,
              message: error.message as string,
              rowName,
            })
          }
        })
      }
    })

    return errorList
  }, [errors, formFieldName, currentData, getRowName])

  // Check if table has any changes (isDirty)
  const hasChanges = useMemo(() => {
    return editedRows.size > 0 || newRows.size > 0 || deletedRows.size > 0
  }, [editedRows.size, newRows.size, deletedRows.size])

  const onSubmit = useCallback(
    (formData: any) => {
      // If onSubmit is provided from outside, use it
      if (onSubmitProp) {
        onSubmitProp(formData)
        return
      }

      // Otherwise, use the default behavior with onSave
      const dataArray = getDataArray(formData)

      const addedData = dataArray.filter(
        (row: T) => newRows.has(row.id) && !deletedRows.has(row.id)
      )

      const editedData = dataArray.filter(
        (row: T) =>
          editedRows.has(row.id) &&
          !newRows.has(row.id) &&
          !deletedRows.has(row.id)
      )

      const deletedData = dataArray.filter((row: T) => deletedRows.has(row.id))

      const allData = dataArray.filter((row: T) => !deletedRows.has(row.id))

      onSave?.({
        added: addedData,
        edited: editedData,
        deleted: deletedData,
        all: allData,
      })
    },
    [editedRows, newRows, deletedRows, onSave, onSubmitProp, formFieldName]
  )

  const handleAddRowClick = useCallback(() => {
    if (!onAddRow) return
    const newRow = onAddRow()
    if (!newRow) return

    const dataArray = currentData
    const newDataArray = [...dataArray, newRow]

    setValue(formFieldName, newDataArray)
    const updatedNewRows = new Set(newRows)
    updatedNewRows.add(newRow.id)
    setNewRows(updatedNewRows)
  }, [onAddRow, currentData, formFieldName, setValue, newRows, setNewRows])

  const handleCloneRowClick = useCallback(
    (rowIndex: number) => {
      if (!onCloneRow) {
        // Default clone behavior
        const rowToClone = currentData[rowIndex]
        const clonedRow = {
          ...rowToClone,
          id: `new-${Date.now()}`,
        } as T

        const newDataArray = [...currentData, clonedRow]
        setValue(formFieldName, newDataArray)
        const updatedNewRows = new Set(newRows)
        updatedNewRows.add(clonedRow.id)
        setNewRows(updatedNewRows)
      } else {
        onCloneRow(rowIndex)
      }
    },
    [onCloneRow, currentData, formFieldName, setValue, newRows, setNewRows]
  )

  const handleDeleteRowClick = useCallback(
    (rowId: string) => {
      if (!onDeleteRow) {
        const updatedDeletedRows = new Set(deletedRows)
        updatedDeletedRows.add(rowId)
        setDeletedRows(updatedDeletedRows)
      } else {
        onDeleteRow(rowId)
      }
    },
    [onDeleteRow, deletedRows, setDeletedRows]
  )

  const getRowStatus = (row: T): RowStatus => {
    if (deletedRows.has(row.id)) return 'deleted'
    if (newRows.has(row.id)) return 'new'
    if (editedRows.has(row.id)) return 'edited'
    return 'unchanged'
  }

  const getStatusIndicatorColor = (status: RowStatus): string => {
    switch (status) {
      case 'deleted':
        return 'bg-red-300'
      case 'new':
        return 'bg-green-300'
      case 'edited':
        return 'bg-blue-300'
      default:
        return 'bg-background'
    }
  }

  const renderCell = (
    column: EditableTableColumn<T>,
    row: T,
    _rowIndex: number,
    actualIndex: number
  ) => {
    const isEdited = editedRows.has(row.id)
    const isNew = newRows.has(row.id)
    const isDeleted = deletedRows.has(row.id)
    const hasError = !!(errors as any)?.[actualIndex]
    const error = hasError ? (errors as any)[actualIndex] : undefined

    const cellProps = {
      value: (row as any)[column.accessorKey],
      row,
      rowIndex: actualIndex,
      isEditMode,
      isEdited,
      isNew,
      isDeleted,
      hasError,
      error,
      register,
      setValue,
      trigger,
      errors,
    }

    if (isEditMode && column.renderEditCell) {
      return column.renderEditCell(cellProps)
    }

    if (column.renderCell) {
      return column.renderCell(cellProps)
    }

    // Default render
    return <span>{(row as any)[column.accessorKey]}</span>
  }

  const getStickyStyle = (
    column: EditableTableColumn<T>,
    columnIndex: number
  ): React.CSSProperties => {
    if (!column.sticky) return {}

    let left = 0
    if (column.sticky === 'left') {
      // Start from status indicator width if present
      if (showStatusIndicator) {
        left = 10 // width of status indicator
      }

      // Calculate left offset based on previous sticky columns
      for (let i = 0; i < columnIndex; i++) {
        if (columns[i].sticky === 'left') {
          left += parseFloat(columns[i].minWidth || '150px')
        }
      }

      return {
        position: 'sticky',
        left: `${left}px`,
        zIndex: 20,
        boxShadow: '2px 0 4px rgba(0,0,0,0.1)',
      }
    }

    if (column.sticky === 'right') {
      // Calculate right offset based on following sticky columns
      let right = 0
      for (let i = columnIndex + 1; i < columns.length; i++) {
        if (columns[i].sticky === 'right') {
          right += parseFloat(columns[i].minWidth || '150px')
        }
      }
      return {
        position: 'sticky',
        right: `${right}px`,
        zIndex: 20,
        boxShadow: '-2px 0 4px rgba(0,0,0,0.1)',
      }
    }

    return {}
  }

  return (
    <div className="space-y-4">
      <Card>
        <CardContent className="p-0">
          <div className="p-4">
            <div className="flex justify-between items-center">
              <div>
                <h2 className="text-display-s font-semibold text-black-500">
                  {title}
                </h2>
                {description && (
                  <p className="text-text-s text-deepNavy-400 mt-1">
                    {description}
                  </p>
                )}
              </div>
              <div className="flex gap-2">
                {!isEditMode ? (
                  <Button onClick={onEdit} size="default" variant="default">
                    <IconEdit className="h-4 w-4 mr-2" />
                    Edit
                  </Button>
                ) : (
                  <>
                    {onAddRow && (
                      <Button
                        onClick={handleAddRowClick}
                        size="default"
                        variant="outline"
                      >
                        <IconPlus className="h-4 w-4 mr-2" />
                        {addButtonLabel}
                      </Button>
                    )}
                    <Button onClick={onCancel} size="default" variant="outline">
                      <IconX className="h-4 w-4 mr-2" />
                      Cancel
                    </Button>
                    <Button
                      onClick={handleSubmit(onSubmit)}
                      size="default"
                      variant="default"
                      disabled={hasValidationErrors || !hasChanges}
                    >
                      <IconDeviceFloppy className="h-4 w-4 mr-2" />
                      Save Changes
                      {hasChanges && (
                        <span className="ml-1 px-2 py-0.5 bg-white/20 rounded-full text-xs">
                          {editedRows.size + newRows.size + deletedRows.size}
                        </span>
                      )}
                    </Button>
                  </>
                )}
              </div>
            </div>
          </div>

          <Separator />

          {/* Validation Errors Warning */}
          {isEditMode && hasValidationErrors && (
            <div className="p-4 bg-red-50 border-b border-red-200">
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 w-6 h-6 bg-red-100 rounded-full flex items-center justify-center">
                  <span className="text-red-600 font-bold text-sm">!</span>
                </div>
                <div className="flex-1">
                  <h3 className="text-sm font-semibold text-red-800 mb-2">
                    {validationErrorCount} Validation Error
                    {validationErrorCount > 1 ? 's' : ''} Found
                  </h3>
                  <p className="text-xs text-red-700 mb-2">
                    Please fix{' '}
                    {showDetailedErrors
                      ? 'the following errors'
                      : 'all validation errors'}{' '}
                    before saving.
                  </p>
                  {showDetailedErrors && validationErrorDetails.length > 0 && (
                    <div className="space-y-1 max-h-32 overflow-y-auto">
                      {validationErrorDetails.slice(0, 10).map((error, idx) => (
                        <div key={idx} className="text-xs text-red-600">
                          • <span className="font-medium">{error.rowName}</span>
                          {' - '}
                          <span className="capitalize">
                            {error.fieldName}
                          </span>: {error.message}
                        </div>
                      ))}
                      {validationErrorDetails.length > 10 && (
                        <div className="text-xs text-red-600 font-medium pt-1">
                          ... and {validationErrorDetails.length - 10} more
                          error
                          {validationErrorDetails.length - 10 > 1 ? 's' : ''}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Filters */}
          {filters.length > 0 && (
            <>
              <div className="p-4 bg-gray-50 border-b">
                <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                  {filters.map((filter, index) => (
                    <div key={index} className="relative">
                      {filter.type === 'search' && (
                        <>
                          <IconSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                          <Input
                            placeholder={filter.placeholder || 'Search...'}
                            value={filter.value}
                            onChange={(e) => filter.onChange(e.target.value)}
                            className="pl-9"
                          />
                        </>
                      )}
                      {filter.type === 'select' && (
                        <Select
                          value={filter.value}
                          onValueChange={filter.onChange}
                        >
                          <SelectTrigger>
                            <SelectValue
                              placeholder={filter.placeholder || 'Select...'}
                            />
                          </SelectTrigger>
                          <SelectContent>
                            {filter.options?.map((option) => (
                              <SelectItem
                                key={option.value}
                                value={option.value}
                              >
                                {option.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}
                    </div>
                  ))}
                </div>
                <div className="mt-2 text-sm text-deepNavy-400">
                  Showing {filteredData.length} of{' '}
                  {currentData.filter((e) => !deletedRows.has(e.id)).length}{' '}
                  entries
                </div>
              </div>
            </>
          )}

          <div className="overflow-x-auto">
            <div
              className="overflow-y-auto"
              style={{ maxHeight: `${tableHeight}px` }}
            >
              <Table className="min-w-max">
                <TableHeader className="border-t sticky top-0 bg-background z-10">
                  <TableRow>
                    {showStatusIndicator && (
                      <TableHead
                        className="w-[10px] p-0 sticky left-0 z-20"
                        style={{
                          position: 'sticky',
                          left: 0,
                          zIndex: 20,
                          backgroundColor: 'hsl(var(--background))',
                        }}
                      ></TableHead>
                    )}
                    {columns.map((column, index) => {
                      const isSticky =
                        column.sticky === 'left' || column.sticky === 'right'
                      const stickyStyle = getStickyStyle(column, index)

                      // Ensure sticky columns always have solid background
                      const headerStyle = isSticky
                        ? {
                            ...stickyStyle,
                            backgroundColor: 'hsl(var(--background))',
                          }
                        : stickyStyle

                      return (
                        <TableHead
                          key={String(column.accessorKey)}
                          className={cn(
                            column.minWidth
                              ? `min-w-[${column.minWidth}]`
                              : 'min-w-[150px]'
                          )}
                          style={headerStyle}
                        >
                          {column.header}
                        </TableHead>
                      )
                    })}
                    {renderActions && (
                      <TableHead
                        className="min-w-[100px] sticky right-0 z-20 shadow-[-2px_0_4px_rgba(0,0,0,0.1)]"
                        style={{
                          position: 'sticky',
                          right: 0,
                          zIndex: 20,
                          backgroundColor: 'hsl(var(--background))',
                          boxShadow: '-2px 0 4px rgba(0,0,0,0.1)',
                        }}
                      >
                        Actions
                      </TableHead>
                    )}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {paginatedData.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={columns.length + (showStatusIndicator ? 1 : 0)}
                        className="text-center py-8 text-deepNavy-400"
                      >
                        {emptyMessage}
                      </TableCell>
                    </TableRow>
                  ) : (
                    paginatedData.map((row, index) => {
                      const actualIndex = filteredData.findIndex(
                        (r) => r.id === row.id
                      )
                      const status = getRowStatus(row)
                      const hasError = !!(errors as any)?.[formFieldName]?.[
                        actualIndex
                      ]

                      return (
                        <TableRow
                          key={row.id}
                          className={cn('h-[72px]', hasError && 'bg-red-50')}
                        >
                          {showStatusIndicator && (
                            <TableCell
                              className={cn(
                                'w-[10px] p-0 sticky left-0 z-20',
                                getStatusIndicatorColor(status)
                              )}
                            ></TableCell>
                          )}
                          {columns.map((column, columnIndex) => {
                            const isSticky =
                              column.sticky === 'left' ||
                              column.sticky === 'right'
                            const stickyStyle = getStickyStyle(
                              column,
                              columnIndex
                            )

                            // Ensure sticky columns always have solid background
                            const cellStyle = isSticky
                              ? {
                                  ...stickyStyle,
                                  backgroundColor: 'hsl(var(--background))',
                                }
                              : stickyStyle

                            return (
                              <TableCell
                                key={String(column.accessorKey)}
                                className={cn(
                                  column.minWidth &&
                                    `min-w-[${column.minWidth}]`
                                )}
                                style={cellStyle}
                              >
                                {renderCell(column, row, index, actualIndex)}
                              </TableCell>
                            )
                          })}
                          {renderActions && isEditMode && (
                            <TableCell
                              className="sticky right-0 z-20 shadow-[-2px_0_4px_rgba(0,0,0,0.1)]"
                              style={{
                                position: 'sticky',
                                right: 0,
                                zIndex: 20,
                                backgroundColor: 'hsl(var(--background))',
                                boxShadow: '-2px 0 4px rgba(0,0,0,0.1)',
                              }}
                            >
                              {renderActions({
                                row,
                                rowIndex: actualIndex,
                                onClone: () => handleCloneRowClick(actualIndex),
                                onDelete: () => handleDeleteRowClick(row.id),
                              })}
                            </TableCell>
                          )}
                        </TableRow>
                      )
                    })
                  )}
                </TableBody>
              </Table>
            </div>
          </div>

          <Separator />

          {/* Pagination */}
          {totalPages > 1 && (
            <>
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={handlePageChange}
              />
              <Separator />
            </>
          )}

          {/* Summary Footer */}
          {summaryItems.length > 0 && (
            <div className="p-4 bg-gray-50">
              <div className="flex justify-between items-center">
                <div className="flex gap-8">
                  {summaryItems.map((item, index) => (
                    <div key={index}>
                      <p className="text-text-s text-deepNavy-400">
                        {item.label}
                      </p>
                      <p
                        className={cn(
                          'text-display-s font-semibold text-black-500',
                          item.className
                        )}
                      >
                        {item.value}
                      </p>
                    </div>
                  ))}
                </div>
                {isEditMode && hasChanges && (
                  <div className="flex items-center gap-2 px-3 py-2 bg-blue-100 text-blue-700 rounded-md">
                    <div className="h-2 w-2 bg-blue-500 rounded-full animate-pulse"></div>
                    <span className="text-sm font-medium">
                      {editedRows.size + newRows.size + deletedRows.size} row
                      {editedRows.size + newRows.size + deletedRows.size > 1
                        ? 's'
                        : ''}{' '}
                      modified
                    </span>
                  </div>
                )}
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
